import { icon, named, text, toggle, url, type Choice } from '../../themes/settingFields';
import { element } from './definition';
import { hover, look, onPhone, pad, paint, radius, round, typeface } from './styleTokens';

// The three ways a page is clicked. Two of them are leaves; the third is a box, because the whole
// point of a block link is that a picture, a title and a price become one target between them.
// Every one lands wearing the store's primary colour, so a repaint reaches every button at once.
//
// All three say where they open with `target`, which is the word the renderer already reads; a
// `newTab` switch beside it only ever looked like it worked. One address covers every destination
// LightFunnels draws a four-way picker for: `/path` is a page, `#band` scrolls, `#popupId` opens
// that popup, and anything else is a link out.

/** Where a click opens: the two values an anchor understands, under the two words LightFunnels uses. */
const OPEN_IN: readonly Choice[] = [['_self', 'This tab'], ['_blank', 'New tab']];

/**
 * LightFunnels splits a click in two: a named PRIMARY ACTION that does something to the order, and a
 * FOLLOW-UP ACTION deciding where the visitor lands once it has run. Both are offered here so the field
 * list reads as theirs, and both describe what this renderer already does rather than promising more —
 * `none` is their "No action selected", and `go-to-page` is `href` plus `target` under its own name.
 *
 * A named primary action is always a submission: paying, capturing a lead, checking a coupon and
 * publishing a review all post the form the button sits in. So naming one makes the button submit,
 * which is the part of their model this renderer can honour today. Dispatching the *specific* action
 * needs a handler registry neither the schema nor the storefront has; it is filed under `agents/lf-spec`.
 */
const PRIMARY_ACTIONS: readonly Choice[] = [
  ['none', 'No action selected'], ['process-payment', 'Process payment'], ['collect-lead', 'Collect lead'],
  ['validate-coupon', 'Validate coupon'], ['publish-review', 'Publish review']
];

/** What happens once the primary action ran. `go-to-page` is what LightFunnels lands a fresh button with. */
const FOLLOW_UP_ACTIONS: readonly Choice[] = [['go-to-page', 'Open link'], ['none', 'Stay on the page']];

/** Whether pressing posts the form the button sits in, or only runs its actions. */
const KINDS: readonly Choice[] = [['button', 'Runs its actions'], ['submit', 'Submits the form'], ['share', 'Shares this page']];

/** Where the icon sits beside the words, or that there is none. */
const ICON_POSITIONS: readonly Choice[] = [['none', 'No icon'], ['before', 'Before the words'], ['after', 'After the words']];

// Describes the two action rows LightFunnels puts above every clickable element's destination.
const actionFields = () =>
  [named('primaryAction', 'Primary action', PRIMARY_ACTIONS), named('followUpAction', 'Follow-up action', FOLLOW_UP_ACTIONS)];

/** What a fresh click lands holding: nothing chosen, and a page to go to afterwards. */
const ACTION_DEFAULTS = { primaryAction: 'none', followUpAction: 'go-to-page' } as const;

/** The button — the element a page is measured by, and the one that must never land looking unfinished. */
export const buttonElement = element('button', {
  label: 'Button',
  description: 'The button a visitor clicks: its words, where it goes, and an optional icon beside them.',
  category: 'Forms',
  icon: 'mouse-pointer-click',
  // Its words come first. The inspector leads with an element's content field when the catalog names one
  // first, and a merchant who opens a button is nine times in ten there to change what it says — not to
  // pick a follow-up action. Behind the words: where it goes, then what pressing it does, then its icon.
  props: [
    text('label', 'Content'), url('href', 'Link'), named('target', 'Open in', OPEN_IN),
    ...actionFields(), named('kind', 'What pressing it does', KINDS),
    icon('icon', 'Icon'), named('iconPosition', 'Icon position', ICON_POSITIONS),
    { ...toggle('hideLabel', 'Show the icon only'), help: 'The words are still read out by screen readers, so the button keeps its name.' }
  ],
  defaults: {
    props: { ...ACTION_DEFAULTS, label: 'Button', href: '', target: '_self', kind: 'button', icon: '', iconPosition: 'none' },
    styles: onPhone(
      hover(
        look({
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
          fontFamily: typeface('body'), fontSize: '16px', fontWeight: 600, lineHeight: 1.2,
          textAlign: 'center', textDecoration: 'none',
          color: paint('onPrimary'), backgroundColor: paint('primary'), cursor: 'pointer',
          transition: 'background-color 300ms ease', ...pad('16px', '24px'), ...radius(round('md'))
        }),
        { backgroundColor: paint('primaryHover') }
      ),
      { fontSize: '15px', paddingTop: '14px', paddingBottom: '14px' }
    )
  }
});

/** A word or phrase inside running copy that goes somewhere else. */
export const linkElement = element('link', {
  label: 'Link',
  description: 'A word or phrase inside your copy that takes the visitor somewhere else.',
  category: 'Forms',
  icon: 'link',
  props: [text('label', 'Label'), url('href', 'Link'), named('target', 'Open in', OPEN_IN)],
  defaults: {
    props: { label: 'Read more', href: '', target: '_self' },
    styles: hover(
      look({
        fontSize: '15px', lineHeight: 1.35, color: paint('primary'),
        textDecoration: 'none', cursor: 'pointer'
      }),
      { color: paint('primaryHover'), textDecoration: 'underline' }
    )
  }
});

/** A whole box that is one link — the card a shopper clicks anywhere on. */
export const blockLinkElement = element('block-link', {
  label: 'BlockLink',
  description: 'A box that is one big link, so a picture, a title and a price are clicked as one card.',
  category: 'Forms',
  icon: 'external-link',
  props: [text('label', 'Accessible label'), url('href', 'Link'), named('target', 'Open in', OPEN_IN), ...actionFields()],
  defaults: {
    props: { ...ACTION_DEFAULTS, href: '', target: '_self', label: '' },
    styles: hover(
      look({
        display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%',
        color: 'inherit', textDecoration: 'none', cursor: 'pointer', transition: 'opacity 300ms ease'
      }),
      { opacity: 0.8 }
    )
  },
});
